import React from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Divider,
	Chip,
	Grid,
	List,
	ListItem,
	ListItemText,
	Paper,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import { PassengerServicesData } from '../helpers/groupServicesByPassenger';
import { formatDate, parseDate } from '../helpers/dateUtils';
import NumberFormat from 'react-number-format';
import { flightAncillaryOptions } from 'features/salesOrder/constants';
import { FlightCo2Summary } from './FlightCo2Summary';
import { CountryCodeEnum } from 'features/common/types';
import { getFlightVatBreakdown, getPassengerVatTotals } from '../helpers/paraguayVatHelper';

interface PassengerSummaryProps {
	passengerData: PassengerServicesData;
	currency: string;
	travelDepartureDate?: string;
	useLocalCurrency?: boolean;
	showReferencesAndTickets?: boolean;
	showCo2Emission?: boolean;
	country?: CountryCodeEnum;
}

export const PassengerSummary = ({
	passengerData,
	currency,
	travelDepartureDate,
	useLocalCurrency = false,
	showReferencesAndTickets = true,
	showCo2Emission = true,
	country,
}: PassengerSummaryProps): JSX.Element => {
	// Función helper para obtener el nombre legible del tipo de ancillary
	const getFlightAncillaryTypeName = (ancillaryType: string | undefined): string => {
		if (!ancillaryType) return '-';
		const option = flightAncillaryOptions.find((opt) => opt.id === ancillaryType);
		return option?.name || ancillaryType;
	};
	const { passenger, services, totalAmount } = passengerData;
	const fullName = `${passenger.name} ${passenger.lastName}`.toUpperCase();

	const isDocumentExpiringSoon = (expirationDate?: string): boolean => {
		if (!expirationDate || !travelDepartureDate) return false;
		try {
			const expDate = parseDate(expirationDate);
			const travelDate = parseDate(travelDepartureDate);
			if (!expDate || !travelDate) return false;
			const sixMonthsFromTravel = new Date(travelDate);
			sixMonthsFromTravel.setMonth(sixMonthsFromTravel.getMonth() + 6);
			return expDate <= sixMonthsFromTravel;
		} catch {
			return false;
		}
	};

	const formatDocument = (doc: any): string => {
		const docType = doc.documentType || 'Documento';
		const docNumber = doc.documentNumber || '';
		const country = doc.country?.code || '';
		return `${docType} ${country} ${docNumber}`.trim();
	};

	const formatFrequentFlyer = (ff: any): string => {
		const airline = ff.airlineCode || '';
		const number = ff.frequentFlyerNumber || '';
		return `${airline} ${number}`.trim();
	};

	return (
		<Card elevation={2} sx={{ mb: 2 }}>
			<CardContent>
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
					<PersonIcon color="primary" />
					<Typography variant="h6" component="h3" fontWeight="bold">
						{fullName}
					</Typography>
					{passenger.mainPassenger && <Chip label="Pasajero Principal" color="primary" size="small" />}
					{passenger.email && (
						<Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
							{passenger.email}
						</Typography>
					)}
				</Box>

				{/* Información adicional del pasajero - Siempre en el mismo orden */}
				<Grid container spacing={2} sx={{ mb: 2 }} wrap="nowrap">
					{/* Columna 1: Fecha de nacimiento */}
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Fecha de nacimiento
						</Typography>
						{passenger.birthdate ? (
							<Typography variant="body2" fontWeight="medium">
								{formatDate(passenger.birthdate)}
							</Typography>
						) : (
							<Typography variant="body2" color="text.disabled">
								-
							</Typography>
						)}
					</Grid>

					{/* Columna 2: Edad al finalizar el viaje */}
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Edad al finalizar el viaje
						</Typography>
						{passenger.ageAtEndTravel !== undefined && passenger.ageAtEndTravel !== null ? (
							<Typography variant="body2" fontWeight="medium">
								{passenger.ageAtEndTravel} años
							</Typography>
						) : (
							<Typography variant="body2" color="text.disabled">
								-
							</Typography>
						)}
					</Grid>

					{/* Columna 3: Números de viajero frecuente */}
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Números de viajero frecuente
						</Typography>
						{passenger.frequentFlyerNumbers && passenger.frequentFlyerNumbers.length > 0 ? (
							passenger.frequentFlyerNumbers.length === 1 ? (
								<Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-word' }}>
									{formatFrequentFlyer(passenger.frequentFlyerNumbers[0])}
								</Typography>
							) : (
								<List dense sx={{ py: 0 }}>
									{passenger.frequentFlyerNumbers.map((ff, index) => (
										<ListItem key={index} sx={{ py: 0, px: 0 }}>
											<ListItemText
												primary={formatFrequentFlyer(ff)}
												primaryTypographyProps={{
													variant: 'body2',
													fontWeight: 'medium',
													sx: { wordBreak: 'break-word' },
												}}
											/>
										</ListItem>
									))}
								</List>
							)
						) : (
							<Typography variant="body2" color="text.disabled">
								-
							</Typography>
						)}
					</Grid>

					{/* Columna 4: Documentos */}
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							Documentos
						</Typography>
						{passenger.documents && passenger.documents.length > 0 ? (
							passenger.documents.length === 1 ? (
								<Box>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
										<Typography
											variant="body2"
											fontWeight="medium"
											color={isDocumentExpiringSoon(passenger.documents[0].expirationDate) ? 'warning.main' : 'inherit'}
											sx={{ wordBreak: 'break-word' }}
										>
											{formatDocument(passenger.documents[0])}
										</Typography>
										{passenger.documents[0].expirationDate && (
											<>
												<Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
													Exp: {formatDate(passenger.documents[0].expirationDate)}
												</Typography>
												{isDocumentExpiringSoon(passenger.documents[0].expirationDate) && (
													<Chip icon={<WarningIcon />} label="Expira pronto" color="warning" size="small" />
												)}
											</>
										)}
									</Box>
								</Box>
							) : (
								<List dense sx={{ py: 0 }}>
									{passenger.documents.map((doc, index) => {
										const isExpiringSoon = isDocumentExpiringSoon(doc.expirationDate);
										return (
											<ListItem key={index} sx={{ py: 0.5, px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
													<Typography
														variant="body2"
														fontWeight="medium"
														color={isExpiringSoon ? 'warning.main' : 'inherit'}
														sx={{ wordBreak: 'break-word' }}
													>
														{formatDocument(doc)}
													</Typography>
													{doc.expirationDate && (
														<>
															<Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
																Exp: {formatDate(doc.expirationDate)}
															</Typography>
															{isExpiringSoon && (
																<Chip icon={<WarningIcon />} label="Expira pronto" color="warning" size="small" />
															)}
														</>
													)}
												</Box>
											</ListItem>
										);
									})}
								</List>
							)
						) : (
							<Typography variant="body2" color="text.disabled">
								-
							</Typography>
						)}
					</Grid>
				</Grid>

				<Divider sx={{ mb: 2 }} />

				{services.length === 0 ? (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
						Este pasajero no tiene servicios asignados
					</Typography>
				) : (
					<>
						<Box sx={{ mb: 2 }}>
							{services.map((service, index) => (
								<Paper key={`${service.serviceKeyId}-${index}`} elevation={1} sx={{ p: 1.5, mb: 1.5 }}>
									{/* Primera línea: Tipo de servicio + Badge (si aplica) + Precio */}
									<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
											<Typography variant="subtitle1" fontWeight="bold">
												{service.serviceName}
											</Typography>
											{service.serviceType === 'Flight' && service.isReissue && (
												<Chip label="Re-emisión" color="warning" size="small" />
											)}
											{service.isCancelled && (
												<Chip label="Servicio cancelado" color="error" size="small" sx={{ fontWeight: 600 }} />
											)}
										</Box>
										<Typography variant="subtitle1" component="div" fontWeight="bold" color="primary">
											<NumberFormat
												value={
													useLocalCurrency && service.saleAmountInLocalCurrency !== undefined
														? service.saleAmountInLocalCurrency
														: service.saleAmount
												}
												displayType="text"
												thousandSeparator="."
												decimalSeparator=","
												decimalScale={2}
												fixedDecimalScale
												prefix={`${
													useLocalCurrency && service.saleAmountInLocalCurrency !== undefined
														? currency
														: service.currency
												} `}
											/>
										</Typography>
									</Box>

									{/* Desglose IVA Paraguay para vuelos con leg ASU */}
									{country &&
										(() => {
											const vatBreakdown = getFlightVatBreakdown(service, country);
											if (!vatBreakdown) return null;
											return (
												<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 0.25 }}>
													<Typography variant="caption" color="text.secondary">
														Base imponible:{' '}
														<NumberFormat
															value={vatBreakdown.taxableBase}
															displayType="text"
															thousandSeparator="."
															decimalSeparator=","
															decimalScale={2}
															fixedDecimalScale
															prefix={`${service.currency} `}
														/>
													</Typography>
													<Typography variant="caption" color="text.secondary">
														IVA (10%):{' '}
														<NumberFormat
															value={vatBreakdown.vat}
															displayType="text"
															thousandSeparator="."
															decimalSeparator=","
															decimalScale={2}
															fixedDecimalScale
															prefix={`${service.currency} `}
														/>
													</Typography>
												</Box>
											);
										})()}

									{/* Texto aclaratorio para servicios cancelados */}
									{service.isCancelled && (
										<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
											No se brindará. Cargo por política de cancelación del hotel.
										</Typography>
									)}

									{/* Segunda línea: Descripción + Destino/Airline/Tipo Ancillary (si aplica) */}
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5 }}>
										{service.serviceType === 'FlightAncillary' && service.flightAncillaryType ? (
											<Typography variant="body2" color="text.secondary">
												<strong>Tipo:</strong> {getFlightAncillaryTypeName(service.flightAncillaryType)}
											</Typography>
										) : (
											<Typography variant="body2" color="text.secondary">
												{service.description}
											</Typography>
										)}
										{service.serviceType === 'Accommodation' && service.destination && (
											<>
												<Typography variant="body2" color="text.secondary">
													•
												</Typography>
												<Typography variant="body2" color="text.secondary">
													<strong>Destino:</strong> {service.destination}
												</Typography>
											</>
										)}
										{service.serviceType === 'Flight' && service.airline && (
											<>
												<Typography variant="body2" color="text.secondary">
													•
												</Typography>
												<Typography variant="body2" color="text.secondary">
													<strong>Aerolínea:</strong> {service.airline}
												</Typography>
											</>
										)}
									</Box>

									{/* Tercera línea: Compartiendo habitación + Localizador (solo para Accommodation) */}
									{service.serviceType === 'Accommodation' &&
										((service.sharingWith && service.sharingWith.length > 0) ||
											(showReferencesAndTickets && service.reference)) && (
											<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5 }}>
												{service.sharingWith && service.sharingWith.length > 0 && (
													<Typography variant="body2" color="text.secondary">
														<strong>Compartiendo habitación con:</strong> {service.sharingWith.join(', ')}
													</Typography>
												)}
												{service.sharingWith &&
													service.sharingWith.length > 0 &&
													showReferencesAndTickets &&
													service.reference && (
														<Typography variant="body2" color="text.secondary">
															•
														</Typography>
													)}
												{showReferencesAndTickets && service.reference && (
													<Typography variant="body2" color="text.secondary">
														<strong>Localizador de reserva:</strong> {service.reference}
													</Typography>
												)}
											</Box>
										)}

									{/* Información adicional para Flight */}
									{service.serviceType === 'Flight' && (
										<Box sx={{ mb: 0.5 }}>
											{showReferencesAndTickets && (service.reference || service.ticket) && (
												<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5 }}>
													{service.reference && (
														<Typography variant="body2" color="text.secondary">
															<strong>Localizador de reserva:</strong> {service.reference}
														</Typography>
													)}
													{service.reference && service.ticket && (
														<Typography variant="body2" color="text.secondary">
															•
														</Typography>
													)}
													{service.ticket && (
														<Typography variant="body2" color="text.secondary">
															<strong>Número de ticket:</strong> {service.ticket}
														</Typography>
													)}
												</Box>
											)}
											{/* Itinerario de vuelo */}
											{service.flightLegs && service.flightLegs.length > 0 && (
												<Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
													<Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
														Itinerario:
													</Typography>
													{service.flightLegs.map((leg: any, legIndex: number) => {
														const flightLegsLength = service.flightLegs?.length || 0;
														return (
															<Box
																key={legIndex}
																sx={{
																	display: 'flex',
																	alignItems: 'center',
																	gap: 1,
																	flexWrap: 'wrap',
																	mb: legIndex < flightLegsLength - 1 ? 0.5 : 0,
																}}
															>
																{/* Logo de la aerolínea */}
																{leg.airline?.image && (
																	<Box
																		component="img"
																		src={leg.airline.image}
																		alt={leg.airline.code || 'Airline'}
																		sx={{
																			height: '20px',
																			width: 'auto',
																			maxWidth: '30px',
																			objectFit: 'contain',
																		}}
																	/>
																)}
																{/* Número de vuelo */}
																<Typography variant="body2" color="text.secondary">
																	<strong>{leg.flightNumber || '-'}</strong>
																</Typography>
																<Typography variant="body2" color="text.secondary">
																	•
																</Typography>
																{/* Origen y destino */}
																<Typography variant="body2" color="text.secondary">
																	{leg.departure?.airport?.code || '-'} → {leg.arrival?.airport?.code || '-'}
																</Typography>
																<Typography variant="body2" color="text.secondary">
																	•
																</Typography>
																{/* Fecha */}
																{leg.departure?.date && (
																	<Typography variant="body2" color="text.secondary">
																		{formatDate(leg.departure.date)}
																	</Typography>
																)}
																<Typography variant="body2" color="text.secondary">
																	•
																</Typography>
																{/* Partida y llegada */}
																{leg.departure?.hour && leg.arrival?.hour && (
																	<>
																		<Typography variant="body2" color="text.secondary">
																			<strong>Partida:</strong> {leg.departure.hour.substring(0, 5)}
																		</Typography>
																		<Typography variant="body2" color="text.secondary">
																			•
																		</Typography>
																		<Typography variant="body2" color="text.secondary">
																			<strong>Arribo:</strong> {leg.arrival.hour.substring(0, 5)}
																		</Typography>
																	</>
																)}
															</Box>
														);
													})}
													{showCo2Emission && <FlightCo2Summary flightLegs={service.flightLegs} />}
												</Box>
											)}
										</Box>
									)}
								</Paper>
							))}
						</Box>

						<Divider sx={{ my: 2 }} />

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								pt: 1,
								px: 1,
							}}
						>
							<Typography variant="h6" component="div" fontWeight="bold">
								Total del Pasajero:
							</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
								<Typography variant="h6" component="div" fontWeight="bold" color="primary">
									<NumberFormat
										value={
											useLocalCurrency && passengerData.totalAmountInLocalCurrency !== undefined
												? passengerData.totalAmountInLocalCurrency
												: totalAmount
										}
										displayType="text"
										thousandSeparator="."
										decimalSeparator=","
										decimalScale={2}
										fixedDecimalScale
										prefix={`${
											useLocalCurrency && passengerData.totalAmountInLocalCurrency !== undefined
												? currency
												: services[0]?.currency || currency
										} `}
									/>
								</Typography>
								{country &&
									(() => {
										const paxVat = getPassengerVatTotals(services, country);
										if (paxVat.taxableBase === 0) return null;
										const displayCurrency = services[0]?.currency || currency;
										return (
											<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 0.5 }}>
												<Typography variant="caption" color="text.secondary">
													Base imponible:{' '}
													<NumberFormat
														value={paxVat.taxableBase}
														displayType="text"
														thousandSeparator="."
														decimalSeparator=","
														decimalScale={2}
														fixedDecimalScale
														prefix={`${displayCurrency} `}
													/>
												</Typography>
												<Typography variant="caption" color="text.secondary">
													IVA (10%):{' '}
													<NumberFormat
														value={paxVat.vat}
														displayType="text"
														thousandSeparator="."
														decimalSeparator=","
														decimalScale={2}
														fixedDecimalScale
														prefix={`${displayCurrency} `}
													/>
												</Typography>
											</Box>
										);
									})()}
							</Box>
						</Box>
					</>
				)}
			</CardContent>
		</Card>
	);
};
